import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { ObjectSchema, number, object, ref, string } from 'yup';

import { disableAutofillBg } from 'src/common/components/constants/disableAutofillBg';
import { LanguageKeys } from 'src/common/constants/languages';
import { IFormAdminUser, IUser } from 'src/interfaces/User';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  handleClose: () => void;
  handleSave: (user: IFormAdminUser) => void;
  user?: IUser;
}

const UserEditorForm = ({ handleClose, handleSave, user }: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { roles } = useSystemOptionsStore(systemOptionsState => systemOptionsState);
  const isEdit = !!user?.id;

  const userSchema: ObjectSchema<IFormAdminUser> = object()
    .shape({
      id: string(),
      name: string().required(t('form.yup.genericRequired')),
      email: string().email().required(t('form.yup.genericRequired')),
      dateFormat: string().required(t('form.yup.genericRequired')),
      timeFormat: string().required(t('form.yup.genericRequired')),
      roleId: number().required(t('form.yup.genericRequired')),
      password: isEdit
        ? string().optional()
        : string()
            .required(t('form.yup.genericRequired'))
            .test('strongPassword', t('form.yup.passwordComplexity'), value => {
              if (!value) return false;
              return (/^(?=.*[a-z])/.test(value) &&
              /^(?=.*[A-Z])/.test(value) &&
              /^(?=.*[0-9])/.test(value) && /^(?=.*[^A-Za-z0-9])/.test(value));
            }),
      newPassword: string()
        .test('strongPassword', t('form.yup.passwordComplexity'), value => {
          if (!value) return true;
          return (/^(?=.*[a-z])/.test(value) &&
          /^(?=.*[A-Z])/.test(value) &&
          /^(?=.*[0-9])/.test(value) && /^(?=.*[^A-Za-z0-9])/.test(value));
        })
        .oneOf([ref('repeatPassword')], t('form.yup.passwordsDontMatch')),
      repeatPassword: string().oneOf([ref('newPassword')], t('form.yup.passwordsDontMatch')),
    })
    .required();

  const defaultValues: IFormAdminUser = user
    ? {
        id: user.id,
        name: user.name,
        email: user.email,
        dateFormat: user.dateFormat,
        timeFormat: user.timeFormat,
        roleId: user.role?.roleId ?? 10,
      }
    : {
        name: '',
        email: '',
        dateFormat: 'YYYY.MM.DD.',
        timeFormat: 'HH:mm',
        roleId: 10,
        password: '',
      };

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
  } = useForm<IFormAdminUser>({
    resolver: yupResolver(userSchema),
    defaultValues,
  });

  const onSubmit: SubmitHandler<IFormAdminUser> = data => {
    handleSave(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            sx={disableAutofillBg}
            label={t('form.name')}
            variant="outlined"
            fullWidth
            margin="dense"
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            sx={disableAutofillBg}
            label={t('form.email')}
            variant="outlined"
            type="email"
            fullWidth
            margin="dense"
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            sx={disableAutofillBg}
            label={t('form.dateFormat')}
            variant="outlined"
            fullWidth
            margin="dense"
            placeholder="YYYY.MM.DD."
            error={!!errors.dateFormat}
            helperText={errors.dateFormat?.message}
            {...register('dateFormat')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            sx={disableAutofillBg}
            label={t('form.timeFormat')}
            variant="outlined"
            fullWidth
            margin="dense"
            placeholder="HH:mm"
            error={!!errors.timeFormat}
            helperText={errors.timeFormat?.message}
            {...register('timeFormat')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="roleId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth margin="dense" size="small" error={!!errors.roleId}>
                <InputLabel id="user-role-label">{t('users.role')}</InputLabel>
                <Select
                  labelId="user-role-label"
                  label={t('users.role')}
                  value={field.value}
                  onChange={e => field.onChange(Number(e.target.value))}
                >
                  {roles.map(role => (
                    <MenuItem key={role.id} value={role.roleId}>
                      {role.i18n?.[currentLang] || role.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.roleId && <FormHelperText>{errors.roleId.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        {!isEdit ? (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              sx={disableAutofillBg}
              autoComplete="new-password"
              label={t('form.password')}
              variant="outlined"
              type="password"
              fullWidth
              margin="dense"
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register('password')}
            />
          </Grid>
        ) : (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                sx={disableAutofillBg}
                autoComplete="new-password"
                label={t('form.newPassword')}
                variant="outlined"
                type="password"
                fullWidth
                margin="dense"
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                {...register('newPassword')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                sx={disableAutofillBg}
                autoComplete="new-password"
                label={t('form.newPasswordRepeat')}
                variant="outlined"
                type="password"
                fullWidth
                margin="dense"
                error={!!errors.repeatPassword}
                helperText={errors.repeatPassword?.message}
                {...register('repeatPassword')}
              />
            </Grid>
          </>
        )}
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1
          }}>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            {t('base.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={!isDirty}>
            {t('form.save')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default UserEditorForm;
